import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import NotificationsDropdown from './NotificationsDropdown';
import SupportChat from './SupportChat';
import lightLogo from '../assets/autoboy_logo2.png';
import darkLogo from '../assets/autoboy_logo3.png';
import { productsAPI } from '../services/api';
import '../assets/css/navbar-restructured.css';
import '../assets/css/mobile-navbar.css';

const Navbar = ({ darkMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { cartCount } = useCart();

  const [sidebarOpen, setSidebarOpen]         = useState(false);
  const [scrolled, setScrolled]               = useState(false);
  const [navHidden, setNavHidden]             = useState(false);
  const [dropdownOpen, setDropdownOpen]       = useState(false);
  const [searchQuery, setSearchQuery]         = useState('');
  const [supportChatOpen, setSupportChatOpen] = useState(false);
  const [searchResults, setSearchResults]     = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading]     = useState(false);
  const [searchExpanded, setSearchExpanded]   = useState(false);
  const [badgePulse, setBadgePulse]           = useState(false);
  const [sidebarSearchFocused, setSidebarSearchFocused] = useState(false);
  const [userCardTilt, setUserCardTilt]       = useState({ x: 0, y: 0 });

  const lastScrollYRef    = useRef(0);
  const lastCartCountRef  = useRef(cartCount);
  const searchTimeoutRef  = useRef(null);
  const searchContainerRef = useRef(null);

  // ── Badge pulse when cart count increases ──────────────────────
  useEffect(() => {
    if (cartCount > lastCartCountRef.current) {
      setBadgePulse(true);
      const t = setTimeout(() => setBadgePulse(false), 600);
      return () => clearTimeout(t);
    }
    lastCartCountRef.current = cartCount;
  }, [cartCount]);

  // ── Scroll: hide-on-down / reveal-on-up + scrolled pill ────────
  useEffect(() => {
    const THRESHOLD = 80;
    const HIDE_DELTA = 60;

    const handleScroll = () => {
      const currentY = window.scrollY;
      const delta    = currentY - lastScrollYRef.current;

      setScrolled(currentY > THRESHOLD);

      // Only hide after scrolling significantly past threshold
      if (currentY > THRESHOLD + HIDE_DELTA && delta > 4) {
        setNavHidden(true);
      } else if (delta < -4) {
        setNavHidden(false);
      }

      lastScrollYRef.current = currentY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ── Dropdown overflow adjustment ────────────────────────────────
  useEffect(() => {
    const adjust = () => {
      document.querySelectorAll('.nav-dropdown-menu').forEach((menu) => {
        menu.style.removeProperty('transform');
        requestAnimationFrame(() => {
          const rect = menu.getBoundingClientRect();
          const vw   = window.innerWidth;
          if (rect.left < 20) {
            const offset = 20 - rect.left;
            menu.style.transform = `translateX(calc(-50% + ${offset}px))`;
          } else if (rect.right > vw - 20) {
            const offset = rect.right - (vw - 20);
            menu.style.transform = `translateX(calc(-50% - ${offset}px))`;
          }
        });
      });
    };

    const onEnter = (e) => {
      if (e.target?.closest?.('.nav-dropdown')) adjust();
    };

    document.addEventListener('mouseover', onEnter, true);
    window.addEventListener('resize', adjust);
    return () => {
      document.removeEventListener('mouseover', onEnter, true);
      window.removeEventListener('resize', adjust);
    };
  }, []);

  // ── Close search on outside click ──────────────────────────────
  useEffect(() => {
    const onOut = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowSearchResults(false);
        setSearchExpanded(false);
      }
    };
    document.addEventListener('mousedown', onOut);
    return () => document.removeEventListener('mousedown', onOut);
  }, []);

  // ── Dashboard URL ───────────────────────────────────────────────
  const getDashboardUrl = useCallback(() => {
    if (!isAuthenticated || !user) return '/auth';
    const userType  = (user.user_type || user.type || 'buyer').toLowerCase();
    const isPremium = user.isPremium || user.premium_status === 'active';
    switch (userType) {
      case 'seller': return isPremium ? '/premium-seller-dashboard' : '/seller-dashboard';
      case 'admin':  return '/admin-dashboard';
      default:       return isPremium ? '/premium-buyer-dashboard' : '/buyer-dashboard';
    }
  }, [isAuthenticated, user]);

  const isPremiumUser = isAuthenticated && user &&
    (user.isPremium || user.premium_status === 'active');

  const getFirstName = () => {
    if (!isAuthenticated || !user) return null;
    return user.first_name || user.firstName || user.username || 'User';
  };

  // ── Handlers ────────────────────────────────────────────────────
  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) logout();
  };

  const handleDashboardClick = (e) => {
    e.preventDefault();
    navigate(getDashboardUrl());
  };

  const toggleSidebar = () => {
    setSidebarOpen((v) => {
      if (v) setDropdownOpen(false);
      return !v;
    });
  };

  const toggleDropdown = () => setDropdownOpen((v) => !v);

  // ── Search ──────────────────────────────────────────────────────
  const handleSearchChange = async (value) => {
    setSearchQuery(value);
    clearTimeout(searchTimeoutRef.current);

    if (value.length > 1) {
      setShowSearchResults(true);
      setSearchLoading(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const res = await productsAPI.getAll({ search: value, limit: 5 });
          setSearchResults((res?.data || []).slice(0, 5));
        } catch {
          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      }, 300);
    } else {
      setShowSearchResults(false);
      setSearchResults([]);
      setSearchLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSearchResults(false);
      setSearchExpanded(false);
      navigate('/shop', { state: { searchQuery: searchQuery.trim() } });
      setSearchQuery('');
      setSidebarOpen(false);
    }
  };

  const handleSearchResultClick = (product) => {
    setShowSearchResults(false);
    setSearchQuery('');
    setSearchExpanded(false);
    navigate(`/product/${product._id || product.id}`);
  };

  // ── Magnetic icon effect ────────────────────────────────────────
  const handleMagnet = (e) => {
    const el   = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const dx   = (e.clientX - (rect.left + rect.width  / 2)) * 0.35;
    const dy   = (e.clientY - (rect.top  + rect.height / 2)) * 0.35;
    el.style.transform = `translate(${dx}px, ${dy}px)`;
  };

  const handleMagnetLeave = (e) => {
    e.currentTarget.style.transform = '';
  };

  // ── User card 3D tilt ───────────────────────────────────────────
  const handleUserCardTilt = (e) => {
    const el   = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const cx   = rect.left + rect.width  / 2;
    const cy   = rect.top  + rect.height / 2;
    const dx   = ((e.clientX - cx) / (rect.width  / 2)) * 6;
    const dy   = ((e.clientY - cy) / (rect.height / 2)) * 6;
    setUserCardTilt({ x: -dy, y: dx });
  };

  const handleUserCardLeave = () => setUserCardTilt({ x: 0, y: 0 });

  // ── Active link helper ──────────────────────────────────────────
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // ── Nav class names ─────────────────────────────────────────────
  const navClass = [
    'navbar',
    scrolled   ? 'scrolled'    : '',
    navHidden  ? 'nav-hidden'  : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <nav className={navClass} id="navbar">
        <div className="nav-container">

          {/* ── Logo ── */}
          <a href="/homepage" className="logo">
            <img src={darkMode ? darkLogo : lightLogo} alt="AutoboyExpress" />
          </a>

          {/* ── Nav Center (desktop: shop + search) ── */}
          <div className="nav-center">
            {!isAuthenticated && (
              <a href="/become-seller" className="become-seller-btn ab-desktop-only">
                <i className="fas fa-store" />
                <span>Become a Seller</span>
              </a>
            )}

            {/* Shop dropdown */}
            <div className="nav-dropdown ab-desktop-only">
              <a href="/shop" className="nav-dropdown-trigger">
                <i className="fas fa-shopping-bag" />
                <span>Shop</span>
                <i className="fas fa-chevron-down dropdown-arrow" />
              </a>
              <div className="nav-dropdown-menu">
                <div className="dropdown-section">
                  <h4 className="dropdown-section-title">Categories</h4>
                  <a href="/shop?category=brand-new"    className="dropdown-link"><i className="fas fa-tag" /><span>Brand New</span></a>
                  <a href="/shop?category=uk-used"      className="dropdown-link"><i className="fas fa-globe" /><span>UK Used</span></a>
                  <a href="/shop?category=good-deal"    className="dropdown-link"><i className="fas fa-fire" /><span>Good Deal</span></a>
                  <a href="/shop?category=nigerian-used" className="dropdown-link"><i className="fas fa-map-marker-alt" /><span>Nigerian Used</span></a>
                  <a href="/swap-deals"                  className="dropdown-link"><i className="fas fa-exchange-alt" /><span>Swap Deal</span></a>
                  <a href="/shop?category=for-sale"     className="dropdown-link"><i className="fas fa-dollar-sign" /><span>For Sale</span></a>
                  <a href="/shop?category=want-to-buy"  className="dropdown-link"><i className="fas fa-search" /><span>Want to Buy</span></a>
                  <a href="/shop?category=random-items" className="dropdown-link"><i className="fas fa-cube" /><span>Random Items</span></a>
                </div>
                <div className="dropdown-section">
                  <h4 className="dropdown-section-title">Device Types</h4>
                  <a href="/shop?type=phones"      className="dropdown-link"><i className="fas fa-mobile-alt" /><span>Smartphones</span></a>
                  <a href="/shop?type=laptops"     className="dropdown-link"><i className="fas fa-laptop" /><span>Laptops</span></a>
                  <a href="/shop?type=tablets"     className="dropdown-link"><i className="fas fa-tablet-alt" /><span>Tablets</span></a>
                  <a href="/shop?type=audio"       className="dropdown-link"><i className="fas fa-volume-up" /><span>Audio</span></a>
                  <a href="/shop?type=headphones"  className="dropdown-link"><i className="fas fa-headphones" /><span>Headphones</span></a>
                  <a href="/shop?type=gaming"      className="dropdown-link"><i className="fas fa-gamepad" /><span>Gaming</span></a>
                  <a href="/shop?type=accessories" className="dropdown-link"><i className="fas fa-plug" /><span>Accessories</span></a>
                </div>
              </div>
            </div>

            {/* Search bar */}
            <div
              className={`search-container${searchExpanded ? ' expanded' : ''}`}
              ref={searchContainerRef}
            >
              <form className="search-wrapper" onSubmit={handleSearch}>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search products, brands…"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => {
                    setSearchExpanded(true);
                    if (searchQuery.length > 1) setShowSearchResults(true);
                  }}
                />
                <button type="submit" className="search-btn">
                  <i className="fas fa-search" />
                </button>
              </form>

              {showSearchResults && (
                <div className="navbar-search-results">
                  {searchLoading ? (
                    <div className="search-loading">
                      <i className="fas fa-spinner fa-spin" />
                      <span>Searching…</span>
                    </div>
                  ) : searchResults.length > 0 ? (
                    <>
                      <div className="search-results-list">
                        {searchResults.map((p) => (
                          <div
                            key={p._id || p.id}
                            className="search-result-item"
                            onClick={() => handleSearchResultClick(p)}
                          >
                            <img
                              src={p.images?.[0] || p.image || 'https://via.placeholder.com/50'}
                              alt={p.title || p.name}
                              className="search-result-img"
                            />
                            <div className="search-result-info">
                              <h4>{p.title || p.name}</h4>
                              <p className="search-result-price">
                                ₦{p.price?.toLocaleString('en-NG') || '0'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="search-view-all">
                        <button onClick={handleSearch} className="view-all-btn">
                          View all for "{searchQuery}"
                          <i className="fas fa-arrow-right" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="search-no-results">
                      <i className="fas fa-search" />
                      <p>No results for "{searchQuery}"</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Nav Right ── */}
          <div className="nav-right">

            {/* User status pill (desktop) */}
            {isAuthenticated ? (
              <a
                href={getDashboardUrl()}
                className="nb-user-pill ab-desktop-only"
                onClick={handleDashboardClick}
                onMouseMove={handleMagnet}
                onMouseLeave={handleMagnetLeave}
              >
                <div className={`nb-user-pill-avatar${isPremiumUser ? ' nb-premium' : ''}`}>
                  {(user?.avatar || user?.profile?.avatar)
                    ? <img src={user.avatar || user.profile.avatar} alt=""
                        onError={e => { e.target.style.display = 'none'; }} />
                    : <i className="fas fa-user" style={{ fontSize: '13px' }} />}
                </div>
                <span className="nb-user-name">Hi, {getFirstName()}</span>
                {isPremiumUser && <i className="fas fa-crown nb-premium-crown" />}
              </a>
            ) : (
              <a href="/auth" className="nav-link ab-desktop-only">
                <i className="fas fa-user-circle" />
                <span>Sign In</span>
              </a>
            )}

            {/* Cart icon with magnetic + pulse badge */}
            <a
              href="/cart"
              className="nav-link ab-desktop-only nb-magnetic"
              style={{ position: 'relative' }}
              onMouseMove={handleMagnet}
              onMouseLeave={handleMagnetLeave}
            >
              <i className="fas fa-shopping-cart" />
              <span>Cart</span>
              {cartCount > 0 && (
                <span className={`nb-cart-badge${badgePulse ? ' nb-badge-pulse' : ''}`}>
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </a>

            {/* Notifications (desktop) */}
            {isAuthenticated && (
              <div className="ab-desktop-only">
                <NotificationsDropdown />
              </div>
            )}

            {/* Help */}
            <a href="/help" className="nav-link ab-desktop-only">
              <i className="fas fa-headset cc-link-icon" />
              <span>Help</span>
            </a>

            {/* Logout (desktop) */}
            {isAuthenticated && (
              <button onClick={handleLogout} className="nb-logout-btn ab-desktop-only">
                <i className="fas fa-sign-out-alt" />
                <span>Logout</span>
              </button>
            )}

            {/* Mobile: notifications only */}
            {isAuthenticated && (
              <div className="ab-mobile-only">
                <NotificationsDropdown />
              </div>
            )}

            {/* Mobile: cart icon */}
            <a
              href="/cart"
              className="nav-link ab-mobile-only"
              style={{ position: 'relative' }}
            >
              <i className="fas fa-shopping-cart" />
              {cartCount > 0 && (
                <span className={`nb-cart-badge${badgePulse ? ' nb-badge-pulse' : ''}`}>
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </a>

            {/* Hamburger */}
            <button className="mobile-menu-btn" onClick={toggleSidebar} aria-label="Menu">
              <div className={`hamburger${sidebarOpen ? ' active' : ''}`}>
                <span />
                <span />
                <span />
              </div>
            </button>
          </div>

        </div>
      </nav>

      {/* ── Mobile Command Center Sidebar ── */}
      {sidebarOpen && (
        <>
          {/* Backdrop */}
          <div className="cc-overlay cc-open" onClick={toggleSidebar} />

          {/* Panel */}
          <div className="cc-panel cc-open">
            {/* Drag Handle */}
            <div className="cc-drag-handle" />

            <div className="cc-panel-inner">

              {/* Header row */}
              <div className="cc-header-bar">
                <img
                  src={darkMode ? darkLogo : lightLogo}
                  alt="AutoboyExpress"
                  className="cc-header-logo"
                />
                <button className="cc-close-btn" onClick={toggleSidebar} aria-label="Close menu">
                  <i className="fas fa-times" />
                </button>
              </div>

              {/* ── Box 1: User card ── */}
              <div
                className={`cc-bento-box ${isAuthenticated ? 'cc-user-card' : 'cc-guest-card'}`}
                onMouseMove={isAuthenticated ? handleUserCardTilt : undefined}
                onMouseLeave={isAuthenticated ? handleUserCardLeave : undefined}
                style={isAuthenticated ? {
                  transform: `perspective(600px) rotateX(${userCardTilt.x}deg) rotateY(${userCardTilt.y}deg)`,
                } : undefined}
              >
                <span className="cc-ghost-label">PROFILE</span>
                {isAuthenticated ? (
                  <>
                    <div className={`cc-user-avatar${isPremiumUser ? ' cc-premium' : ''}`}>
                      {(user?.avatar || user?.profile?.avatar)
                        ? <img src={user.avatar || user.profile.avatar} alt=""
                            onError={e => { e.target.style.display = 'none'; }} />
                        : <i className="fas fa-user" />}
                    </div>
                    <div className="cc-user-meta">
                      <p className="cc-user-name">Hi, {getFirstName()}</p>
                      <p className="cc-user-email">{user?.email || ''}</p>
                      {isPremiumUser && (
                        <span className="cc-user-badge">
                          <i className="fas fa-crown" style={{ fontSize: '9px' }} />
                          Premium
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="cc-guest-icon"><i className="fas fa-user" /></div>
                    <p className="cc-guest-title">Welcome to AutoboyExpress</p>
                    <p className="cc-guest-sub">Sign in for the full experience</p>
                    <a href="/auth" className="cc-signin-btn" onClick={toggleSidebar}>
                      <i className="fas fa-sign-in-alt" />
                      Sign In / Register
                    </a>
                  </>
                )}
              </div>

              {/* ── Box 2: Search ── */}
              <div className="cc-bento-box cc-search-box">
                <form
                  className={`cc-search-form${sidebarSearchFocused ? ' cc-focused' : ''}`}
                  onSubmit={handleSearch}
                >
                  <i className="fas fa-search" />
                  <input
                    type="text"
                    className="cc-search-input"
                    placeholder="Search products, brands…"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onFocus={() => setSidebarSearchFocused(true)}
                    onBlur={() => setSidebarSearchFocused(false)}
                  />
                  <button type="submit" className="cc-search-btn">
                    <i className="fas fa-arrow-right" />
                  </button>
                </form>
              </div>

              {/* ── Box 3: Navigation ── */}
              <div className="cc-bento-box">
                <span className="cc-ghost-label">NAV</span>
                <p className="cc-bento-label">Navigate</p>
                <div className="cc-nav-list">
                  {isAuthenticated && (
                    <a
                      href={getDashboardUrl()}
                      className={`cc-nav-link${isActive(getDashboardUrl()) ? ' cc-active' : ''}`}
                      onClick={(e) => { handleDashboardClick(e); toggleSidebar(); }}
                    >
                      <i className="fas fa-th-large cc-link-icon" />
                      <span>Dashboard</span>
                      <i className="fas fa-chevron-right cc-link-chevron" />
                    </a>
                  )}

                  <a
                    href="/shop"
                    className={`cc-nav-link${isActive('/shop') ? ' cc-active' : ''}`}
                    onClick={toggleSidebar}
                  >
                    <i className="fas fa-shopping-bag cc-link-icon" />
                    <span>Shop All</span>
                    <i className="fas fa-chevron-right cc-link-chevron" />
                  </a>

                  <a
                    href="/cart"
                    className={`cc-nav-link${isActive('/cart') ? ' cc-active' : ''}`}
                    onClick={toggleSidebar}
                  >
                    <i className="fas fa-shopping-cart cc-link-icon" />
                    <span>Cart</span>
                    {cartCount > 0 && (
                      <span className="cc-link-badge">{cartCount > 99 ? '99+' : cartCount}</span>
                    )}
                    <i className="fas fa-chevron-right cc-link-chevron" />
                  </a>

                  <a
                    href="/wishlist"
                    className={`cc-nav-link${isActive('/wishlist') ? ' cc-active' : ''}`}
                    onClick={toggleSidebar}
                  >
                    <i className="fas fa-heart cc-link-icon" />
                    <span>Wishlist</span>
                    <i className="fas fa-chevron-right cc-link-chevron" />
                  </a>

                  <a
                    href="/orders"
                    className={`cc-nav-link${isActive('/orders') ? ' cc-active' : ''}`}
                    onClick={toggleSidebar}
                  >
                    <i className="fas fa-box cc-link-icon" />
                    <span>My Orders</span>
                    <i className="fas fa-chevron-right cc-link-chevron" />
                  </a>

                  {!isAuthenticated && (
                    <a href="/become-seller" className="cc-nav-link" onClick={toggleSidebar}>
                      <i className="fas fa-store cc-link-icon" />
                      <span>Become a Seller</span>
                      <i className="fas fa-chevron-right cc-link-chevron" />
                    </a>
                  )}
                </div>
              </div>

              {/* ── Box 4: Shop Categories grid ── */}
              <div className="cc-bento-box">
                <span className="cc-ghost-label">SHOP</span>
                <p className="cc-bento-label">Categories</p>
                <div className="cc-cat-grid">
                  <a href="/shop?category=brand-new"     className="cc-cat-item" onClick={toggleSidebar}><i className="fas fa-tag" />Brand New</a>
                  <a href="/shop?category=uk-used"       className="cc-cat-item" onClick={toggleSidebar}><i className="fas fa-globe" />UK Used</a>
                  <a href="/shop?category=good-deal"     className="cc-cat-item" onClick={toggleSidebar}><i className="fas fa-fire" />Good Deal</a>
                  <a href="/shop?category=nigerian-used" className="cc-cat-item" onClick={toggleSidebar}><i className="fas fa-map-marker-alt" />Nigerian Used</a>
                  <a href="/swap-deals"                   className="cc-cat-item" onClick={toggleSidebar}><i className="fas fa-exchange-alt" />Swap Deal</a>
                  <a href="/shop?category=for-sale"      className="cc-cat-item" onClick={toggleSidebar}><i className="fas fa-dollar-sign" />For Sale</a>
                  <a href="/shop?category=want-to-buy"   className="cc-cat-item" onClick={toggleSidebar}><i className="fas fa-search" />Want to Buy</a>
                  <a href="/shop?category=random-items"  className="cc-cat-item" onClick={toggleSidebar}><i className="fas fa-cube" />Random Items</a>
                </div>

                <p className="cc-bento-label" style={{ marginTop: '1rem' }}>Device Types</p>
                <div className="cc-cat-grid">
                  <a href="/shop?type=phones"      className="cc-cat-item" onClick={toggleSidebar}><i className="fas fa-mobile-alt" />Smartphones</a>
                  <a href="/shop?type=laptops"     className="cc-cat-item" onClick={toggleSidebar}><i className="fas fa-laptop" />Laptops</a>
                  <a href="/shop?type=tablets"     className="cc-cat-item" onClick={toggleSidebar}><i className="fas fa-tablet-alt" />Tablets</a>
                  <a href="/shop?type=audio"       className="cc-cat-item" onClick={toggleSidebar}><i className="fas fa-volume-up" />Audio</a>
                  <a href="/shop?type=headphones"  className="cc-cat-item" onClick={toggleSidebar}><i className="fas fa-headphones" />Headphones</a>
                  <a href="/shop?type=gaming"      className="cc-cat-item" onClick={toggleSidebar}><i className="fas fa-gamepad" />Gaming</a>
                  <a href="/shop?type=accessories" className="cc-cat-item" onClick={toggleSidebar}><i className="fas fa-plug" />Accessories</a>
                </div>
              </div>

              {/* ── Box 5: Support & Account ── */}
              <div className="cc-bento-box">
                <span className="cc-ghost-label">MORE</span>
                <p className="cc-bento-label">Support</p>
                <div className="cc-nav-list">
                  <a
                    href="/help"
                    className={`cc-nav-link${isActive('/help') ? ' cc-active' : ''}`}
                    onClick={toggleSidebar}
                  >
                    <i className="fas fa-headset cc-link-icon" />
                    <span>Help Center</span>
                    <i className="fas fa-chevron-right cc-link-chevron" />
                  </a>
                  <a
                    href="/support"
                    className={`cc-nav-link${isActive('/support') ? ' cc-active' : ''}`}
                    onClick={toggleSidebar}
                  >
                    <i className="fas fa-comment-dots cc-link-icon" />
                    <span>Customer Support</span>
                    <i className="fas fa-chevron-right cc-link-chevron" />
                  </a>
                </div>

                {isAuthenticated && (
                  <button
                    onClick={() => { toggleSidebar(); handleLogout(); }}
                    className="cc-logout-btn"
                    style={{ marginTop: '8px' }}
                  >
                    <i className="fas fa-sign-out-alt" />
                    Sign Out
                  </button>
                )}
              </div>

            </div>
          </div>
        </>
      )}

      {/* Support Chat */}
      <SupportChat isOpen={supportChatOpen} onClose={() => setSupportChatOpen(false)} />
    </>
  );
};

export default Navbar;
